import {
  makeObservable,
  observable,
  computed,
  action,
  runInAction,
} from "mobx";
import { Brand } from "./types";
import RootStore from "../RootStore";
import BrandingService from "./BrandingService";
import brandsCatalog, { DEFAULT_BRAND_KEY } from "./brandsCatalog";
import { Dictionary } from "../interfaces/Dictionary";

class BrandingStore {
  // Brand key resolved by the backend for the current host (e.g. "careium").
  // undefined until the first resolution completes; set to the default brand when the
  // branding API is unreachable so the app still renders a coherent theme.
  resolvedBrandKey: string | undefined;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.resolvedBrandKey = undefined;

    makeObservable(this, {
      resolvedBrandKey: observable,
      customerKey: computed,
      currentAboutContent: computed,
      currentTitle: computed,
      currentFavicon: computed,
      currentHomeImage: computed,
      currentPartnerLink: computed,
      currentLogo: computed,
      currentLogoSize: computed,
      currentCss: computed,
      currentLoginLanguage: computed,
      currentSecondaryLanguages: computed,
      isForgotPasswordEnabled: computed,
      getBranding: action,
    });
  }

  async getBranding() {
    try {
      const brandKey = await BrandingService.getBrandName(
        globalThis.window.location.href,
      );
      runInAction(() => {
        // Defensive: if the backend resolves a brand the bundle does not know yet
        // (e.g. a new brand deployed server-side first), fall back to the default.
        this.resolvedBrandKey =
          brandKey && brandKey in brandsCatalog ? brandKey : DEFAULT_BRAND_KEY;
      });
    } catch (error) {
      // Resilience: render the default theme rather than leaving the app unbranded
      // when the branding API is unreachable.
      console.error("Error fetching branding content:", error);
      runInAction(() => {
        this.resolvedBrandKey = DEFAULT_BRAND_KEY;
      });
    }
  }

  private get brand(): Brand | undefined {
    if (this.resolvedBrandKey === undefined) {
      return undefined;
    }

    return (
      brandsCatalog[this.resolvedBrandKey] ?? brandsCatalog[DEFAULT_BRAND_KEY]
    );
  }

  // Resolved brand key, defaulting to "default" until resolved / when unknown so that
  // i18n keys, the SSO link map and support lookups always receive a concrete key.
  get customerKey() {
    return this.resolvedBrandKey ?? DEFAULT_BRAND_KEY;
  }

  get isForgotPasswordEnabled() {
    return this.brand?.enableForgotPassword;
  }

  get currentAboutContent() {
    const about = this.brand?.about ?? brandsCatalog[DEFAULT_BRAND_KEY].about;
    const title = this.currentTitle;

    if (!about || !title) {
      return undefined;
    }

    const parsed: Dictionary<string> = {};
    Object.keys(about).forEach((key) => {
      parsed[key] = about[key].replaceAll("%%%title%%%", title);
    });

    return parsed;
  }

  get currentTitle() {
    return this.brand?.title;
  }

  get currentFavicon() {
    return this.brand?.ico;
  }

  get currentHomeImage() {
    return this.brand?.homeImage;
  }

  get currentPartnerLink() {
    return this.brand?.partnerLink;
  }

  get currentLogo() {
    return this.brand?.logo;
  }

  get currentLogoSize() {
    return this.brand?.logoSize;
  }

  get currentCss() {
    return this.brand?.css;
  }

  get currentLoginLanguage() {
    return this.brand?.loginLanguage;
  }

  get currentSecondaryLanguages() {
    return this.brand?.secondaryLanguages;
  }
}

export default BrandingStore;
